/**
 * Free / paid product surface helpers (Phase 11).
 *
 * Free plan: Preliminary only, no formal reports, system "Free Tier"
 * project only. On-screen results are intentionally reduced.
 */

import {
	SubscriptionTier,
	type EntitlementSnapshot,
} from "@flaha/shared-types";

/** Stable project code for the Free system workspace (backend). */
export const FREE_TIER_PROJECT_CODE = "FREE_TIER";

export function isFreePlan(entitlements: EntitlementSnapshot | null | undefined): boolean {
	if (!entitlements) return false;
	if (entitlements.grandfathered) return false;
	return entitlements.tier === SubscriptionTier.FREE;
}

/** Free users may create user projects only when maxProjects !== 0. */
export function canCreateUserProjects(
	entitlements: EntitlementSnapshot | null | undefined
): boolean {
	if (!entitlements) return true;
	if (entitlements.grandfathered) return true;
	if (entitlements.maxProjects === 0) return false;
	return true;
}

export function canGenerateFormalReports(
	entitlements: EntitlementSnapshot | null | undefined
): boolean {
	if (!entitlements) return true;
	if (entitlements.grandfathered) return true;
	return (
		entitlements.allowStandardReport || entitlements.allowCustomReport
	);
}

/**
 * Free Preliminary surface: texture triangle + basic physics numbers only.
 * Hide full scientific suite, agronomic interpretation cards, and report CTAs.
 */
export function freeResultsSurface(
	entitlements: EntitlementSnapshot | null | undefined
): {
	showAgronomicInterpretation: boolean;
	showConstraintPanels: boolean;
	showFullScientificCharts: boolean;
	showTextureTriangleOnly: boolean;
	showReportActions: boolean;
	showChemistryDetail: boolean;
} {
	const free = isFreePlan(entitlements);
	if (free) {
		return {
			showAgronomicInterpretation: false,
			showConstraintPanels: false,
			showFullScientificCharts: false,
			showTextureTriangleOnly: true,
			showReportActions: false,
			showChemistryDetail: false,
		};
	}
	return {
		showAgronomicInterpretation: true,
		showConstraintPanels: true,
		showFullScientificCharts: true,
		showTextureTriangleOnly: false,
		showReportActions: canGenerateFormalReports(entitlements),
		showChemistryDetail: true,
	};
}
